// src/app/shared/interceptors/base-url.interceptor.ts
import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { SKIP_BASE_URL } from '../tokens/http-context.tokens';
import { v4 as uuid4 } from 'uuid';

import dbJson from '../../../../db.json';
import routesJson from '../../../../routes.json';
import { TraceIdService } from '../services/trace-id-service';
import { ConfigLoaderService } from '../services/config-loader.service';

type DbType = typeof dbJson;
type RoutesType = { [pattern: string]: string };
const routes: RoutesType = routesJson as RoutesType;

// 依 Port 分流的 API 第一層路徑
const apiUrlKeys = [
  'member',
  'appointment',
  'healthcheckplan',
  'report',
  'maintenance', // 新增 maintenance 模組
  'notify',
  'validation',
  'tasks',
] as const;
export type ApiKey = (typeof apiUrlKeys)[number];

@Injectable()
export class BaseUrlInterceptor implements HttpInterceptor {
  constructor(
    private configLoaderService: ConfigLoaderService,
    private traceIdService: TraceIdService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    let traceId = this.traceIdService.getCurrentTraceId();

    if (!traceId) {
      traceId = this.generateTraceId();
      this.traceIdService.setTraceId(traceId);
    }

    // 靜態資源：assets/… 或 /assets/… 都排除
    if (req.url.startsWith('/assets/') || req.url.startsWith('assets/')) {
      return next.handle(req);
    }

    // 設定檔請求也要排除，避免循環依賴
    if (req.url.includes('/configs/')) {
      return next.handle(req);
    }

    const config = this.configLoaderService.getConfig();

    if (config?.callAPI && config.API_BASE_URL) {
      // 如果使用正式api環境, API_BASE_URL有分port會是物件, 真正使用http request呼叫API
      // 加入 traceId
      const reqWithTrace = req.clone({
        setHeaders: { 'X-Trace-Id': traceId }
      });
      return this.apiUrlConcatenate(reqWithTrace, next, config.API_BASE_URL);
    } else {
      // 如果使用果思dev模擬環境：直接從 db.json 回傳 mock 資料
      const mockData = this.getMockData(req.url);
      return of(new HttpResponse({ status: 200, body: mockData }));
    }
  }

  private apiUrlConcatenate(req: HttpRequest<any>, next: HttpHandler, apiBaseUrls: Partial<Record<ApiKey, string>>): Observable<HttpEvent<any>> {
    // 呼叫端顯式要求跳過
    if (req.context.get(SKIP_BASE_URL)) {
      return next.handle(req);
    }

    // 絕對網址：http://、https:// 或 // 開頭
    if (/^(?:https?:)?\/\//i.test(req.url)) {
      return next.handle(req);
    }

    // 依第一段 path 決定 baseUrl
    const firstSeg = req.url.match(/^\/([^\/]+)/)?.[1] as string | undefined;

    // 只有列在 apiUrlKeys 裡的才處理；否則直接放行
    if (!firstSeg || !(apiUrlKeys as readonly string[]).includes(firstSeg)) {
      return next.handle(req);
    }

    const baseUrl = apiBaseUrls[firstSeg as ApiKey];
    if (!baseUrl) {
      return next.handle(req);
    }

    // 移除第一段firstSeg路徑，避免重複
    // 但對於 chatbot 與 maintenance，保留完整的 endpoint
    const pathWithoutFirstSeg = (firstSeg === 'tasks' || firstSeg === 'maintenance') 
      ? req.url 
      : req.url.replace(/^\/[^\/]+/, '');
    
    const apiReq = req.clone({
      url: `${baseUrl}${pathWithoutFirstSeg}`,
    });
    return next.handle(apiReq);
  }

  private getMockData(url: string): any {
    const db: DbType = dbJson;
    // 根據routes.json中的路由模式匹配db.json中的數據
    let key: keyof DbType | '' = '';
    for (const pattern in routes) {
      const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$');
      if (regex.test(url)) {
        key = routes[pattern].replace(/^\//, '').split('?')[0] as keyof DbType;
        break;
      }
    }
    if (!key) {
      key = url.replace(/^\//, '').split('?')[0] as keyof DbType;
    }
    return db[key];
  }

  // 生成 traceId
  private generateTraceId(): string {
    return uuid4();
  }
}
